import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  Input,
  inject,
} from '@angular/core';
import type { OnInit } from '@angular/core';
import { KeyboardService } from '@freelancer/keyboard';
import { FreelancerAsyncPipeModule } from '@freelancer/pipes';
import { FlexAlign, FlexJustify } from '@freelancer/ui/flex';
import { StickyBehaviour, StickyPosition } from '@freelancer/ui/sticky';
import type { Observable } from 'rxjs';
import {
  StickyFooterBackgroundColor,
  StickyFooterPosition,
} from './sticky-footer.types';
import { StickyModule } from '../sticky/sticky.module';

@Component({
  standalone: true,
  imports: [FreelancerAsyncPipeModule, NgTemplateOutlet, StickyModule],
  selector: 'fl-sticky-footer',
  template: `
    @if (position === StickyFooterPosition.FIXED) {
      <div
        class="StickyFooterContainer"
        [attr.data-full-width]="fullWidth"
        [flSticky]="true"
        [flStickyStatic]="stickyStatic"
        [flStickyPosition]="StickyPosition.BOTTOM"
        [flStickyBehaviour]="StickyBehaviour.ALWAYS"
      >
        <ng-container *ngTemplateOutlet="content"></ng-container>
      </div>
    }

    @if (position === StickyFooterPosition.ABSOLUTE) {
      <ng-container *ngTemplateOutlet="content"></ng-container>
    }

    <ng-template #content>
      <div
        class="StickyFooter"
        [class.IsKeyboardOpen]="isKeyboardOpen$ | flAsync"
        [class.IsHidden]="(isKeyboardOpen$ | flAsync) && hideOnKeyboardActive"
        [attr.data-position]="position"
        [attr.data-box-shadow]="boxShadow"
        [attr.data-space-between]="spaceBetween"
        [attr.data-background-color]="backgroundColor"
        [attr.data-flex-align]="flexAlign"
        [attr.data-flex-justify]="flexJustify"
      >
        <ng-content></ng-content>
      </div>
    </ng-template>
  `,
  styleUrls: ['./sticky-footer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StickyFooterComponent implements OnInit {
  private keyboardService = inject(KeyboardService);

  StickyFooterPosition = StickyFooterPosition;
  StickyBehaviour = StickyBehaviour;
  StickyPosition = StickyPosition;

  isKeyboardOpen$: Observable<boolean>;

  @Input() position = StickyFooterPosition.ABSOLUTE;
  @Input() boxShadow = true;
  @Input() stickyStatic = false;
  @Input() fullWidth = true;
  @Input() spaceBetween = false;
  @Input() flexAlign: FlexAlign = 'flex-end';
  @Input() flexJustify: FlexJustify = 'flex-end';

  // Hide when virtual keyboard is open
  // Note: This only for native apps
  @Input() hideOnKeyboardActive = true;
  @Input() backgroundColor = StickyFooterBackgroundColor.DEFAULT;

  ngOnInit(): void {
    this.isKeyboardOpen$ = this.keyboardService.isOpen();
  }
}
